import React from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';

import {
  InputTextWithLabel,
  InputBaseContainer,
  CheckboxWithLabel,
  ButtonWithLoader,
} from '../molecules';
import { SubMenuLink } from '../constants/menu';

import { VocabularyDataType } from '../api/vocabulary';

import { FormServerError } from '../types';

import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';
import isYupFieldRequired from '../utils/isYupFieldRequired';

const labels = {
  title: 'Title',
  active: 'Active',
};

const schema = yup.object().shape({
  title: yup.string().required().label(labels.title),
  isActive: yup.boolean().label(labels.active),
});

const VocabularyForm = ({
  onSubmit,
  modalError,
  initialValues,
  addButtonText = 'Add new item',
  updateButtonText = 'Update',
  initialErrors,
  activitiesQuery,
}: Props) => {
  const {
    register,
    handleSubmit,
    errors,
    setError,
    control,
    formState,
  } = useForm<VocabularyDataType>({
    resolver: yupResolver(schema),
    defaultValues:
      initialValues || ({} as VocabularyDataType),
  });

  const isDeleted = initialValues?.isDeleted;

  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<VocabularyDataType>(
        initialErrors,
      ).forEach(({ name, types }) =>
        setError(name, { types }));
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  const middleware = (
    submitFn: (data: VocabularyDataType) => void,
  ) => (data: VocabularyDataType) => {
    submitFn({
      ...data,
      id: initialValues?.id,
      isDeleted: initialValues?.isDeleted,
      vocabularyEntity: activitiesQuery.id,
    });
  };

  return (
    <Container
      disabled={isDeleted || (isSubmitting && !modalError)}
    >
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(middleware(onSubmit))}
      >
        <InputBaseContainer>
          <InputTextWithLabel
            name="title"
            error={errors.title}
            inputRef={register}
            label={labels.title}
            isRequired={isYupFieldRequired('title', schema)}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="isActive"
            control={control}
            valueName="checked"
            defaultValue={initialValues?.isActive ?? true}
            render={({ onChange, onBlur, value }) => (
              <CheckboxWithLabel
                error={errors.isActive}
                label={labels.active}
                checked={value}
                onBlur={onBlur}
                onChange={(e, flag) => onChange(flag)}
              />
            )}
          />
        </InputBaseContainer>
        {!isDeleted && (
          <ButtonWithLoader
            disabled={isSubmitting && !modalError}
            loading={isSubmitting && !modalError}
            type="submit"
          >
            {initialValues
              ? updateButtonText
              : addButtonText}
          </ButtonWithLoader>
        )}
      </FormContainer>
    </Container>
  );
};

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

interface Props {
  addButtonText?: string;
  updateButtonText?: string;
  initialValues?: VocabularyDataType;
  onSubmit: (data: VocabularyDataType) => void;
  initialErrors?: FormServerError<VocabularyDataType>;
  activitiesQuery?: SubMenuLink;
  modalError: boolean;
}

export default VocabularyForm;
